import React, { useState, useRef, useEffect, FunctionComponent } from 'react';

interface DropdownMenuProps {
    options: string[];
    onSelect: (option: string) => void;
    children: React.ReactNode;
    setIsMenuOpen: (isopen: boolean) => void;
    backgroundColor?: string;
    hoverColor?: string;
    maxWidth?: string | 'fit-content'; // e.g., "5vw", "200px", or "fit-content"
}

const DropDown: FunctionComponent<DropdownMenuProps> = ({ options, onSelect, children, setIsMenuOpen, backgroundColor, hoverColor, maxWidth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const [opacity, setOpacity] = useState(1);
  const [buttonWidth, setButtonWidth] = useState(0);

  const toggleDropdown = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
      });
      setButtonWidth(rect.width);
    }
    setOpacity(isOpen ? 0 : 1);
    setIsMenuOpen(!isOpen);
    if (isOpen) {
      setTimeout(() => {
        setIsOpen(false);
      }, 250);
    } else {
      setIsOpen(true);
    }
  };

  const handleSelect = (event: React.MouseEvent, option: string) => {
    event.stopPropagation();
    onSelect(option);
    setOpacity(0);
    setIsMenuOpen(false);
    setTimeout(() => {
      setIsOpen(false);
    }, 250);
  };

  const closeMenu = (event: React.MouseEvent) => {
    setOpacity(0);
    setIsMenuOpen(false);
    setTimeout(() => {
      setIsOpen(false);
    }, 250);
  };

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (
            buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
            dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
        ) {
            setOpacity(0);
            setIsMenuOpen(false);
            setTimeout(() => {
              setIsOpen(false);
            }, 250);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="fadeInPointFive h-full w-full">
      <button
        className="w-full h-full flex justify-center items-center border-none shadow-none m-0 transition-colors duration-300 hover:bg-gray-700/90 active:bg-gray-600/90"
        style={{
          backgroundColor: 'transparent',
          padding: '0.5vh',
          borderRadius: '1vh',
          ...(hoverColor && { '--hover-color': hoverColor } as React.CSSProperties),
        }}
        ref={buttonRef}
        onClick={toggleDropdown}
      >
        {children}

        {isOpen && (
          <ul
            className="fixed m-0 p-0 list-none text-white/90 cursor-pointer overflow-auto z-[9999] shadow-lg transition-opacity duration-250 ease-in"
            ref={dropdownRef}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              opacity: opacity,
              width: maxWidth === 'fit-content' ? 'max-content' : (maxWidth || buttonWidth),
              minWidth: maxWidth === 'fit-content' ? buttonWidth : undefined,
              maxWidth: maxWidth && maxWidth !== 'fit-content' ? maxWidth : undefined,
              maxHeight: "25vh",
              backgroundColor: backgroundColor || "rgba(47, 47, 47, 0.9)",
              borderRadius: "0.5vh",
              boxShadow: "0 0 1vh rgba(0, 0, 0, 0.5)",
            }}
            onMouseLeave={(e) => closeMenu(e)}
          >
            {[...options].map((option) => (
              <li
                key={option}
                onClick={(e) => handleSelect(e, option)}
                className="font-medium transition-colors duration-300 hover:bg-white/20"
                style={{
                  padding: "1vh 1vh",
                  fontSize: "1.25vh",
                  borderRadius: "0.5vh",
                }}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </button>
    </div>
  );
};

export default DropDown;
